import { useCallback, useState } from "react"
import type { DTAVersion } from "../../../types/dta"
import { useCreateVersion } from "../../../hooks/queries/useDtas"
import { parseApiErrorMessage } from "../../../utils/apiErrors"

/**
 * Manages the amendment (create new version) modal, composing the create-version mutation with the
 * modal's local form state. The mutation owns the cache write and invalidation; this hook forwards
 * the created version to the page so it can update selection and navigation.
 */
export function useAmendment(
  dtaId: string | undefined,
  _versions: DTAVersion[],
  onSuccess: (newVersion: DTAVersion) => void,
) {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [reason, setReason] = useState("")
  const [uploadId, setUploadId] = useState<string | undefined>(undefined)
  const createVersion = useCreateVersion(dtaId ?? "")
  const { reset: resetCreateVersion } = createVersion

  const openModal = useCallback(() => setIsModalOpen(true), [])
  const closeModal = useCallback(() => {
    setIsModalOpen(false)
    setUploadId(undefined)
    resetCreateVersion()
  }, [resetCreateVersion])

  const handleAmend = useCallback(async () => {
    if (!dtaId || !reason) return
    try {
      const newVersion = await createVersion.mutateAsync({
        change_reason: reason,
        change_source: uploadId ? "upload" : "manual",
        upload_id: uploadId,
      })
      setIsModalOpen(false)
      setReason("")
      setUploadId(undefined)
      onSuccess(newVersion)
    } catch {
      // Surfaced inline in the modal via `error`; a toast would render behind the modal backdrop.
    }
  }, [dtaId, reason, uploadId, onSuccess, createVersion])

  return {
    isModalOpen,
    openModal,
    closeModal,
    reason,
    setReason,
    uploadId,
    setUploadId,
    loading: createVersion.isPending,
    error: createVersion.error
      ? parseApiErrorMessage(createVersion.error, "Amendment failed")
      : null,
    handleAmend,
  }
}
